import { useRef, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { MAX_COL_PX, MIN_COL_PX } from './columnWidths'
import s from './resizer.module.css'

/**
 * The grip on a column's trailing edge.
 *
 * ── It is a real control, not a div with a mousedown ────────────────────────
 * `role="separator"` with `aria-orientation="vertical"` and the aria-value
 * triple is the ARIA pattern for exactly this, so a screen reader announces the
 * column, its current width and its bounds. ← / → resize by a step, Shift by a
 * coarse one, Home resets. Every list in this console was mouse-only once
 * (UX-02 / WCAG 2.1.1) and a resize handle is the easiest place in the product
 * to repeat that mistake, because dragging is how everyone tests it.
 *
 * ── Pointer events, with capture ────────────────────────────────────────────
 * `setPointerCapture` is what makes a drag survive the pointer leaving the 6px
 * strip — without it the column stops resizing the moment you move faster than
 * React re-renders, which reads as a janky handle rather than a lost capture.
 * It also gives touch and pen for free, where a mousedown/mousemove pair does
 * not.
 *
 * The gesture is measured from the pointer's ORIGIN and the width at grab time,
 * never from the previous frame's width: accumulating deltas drifts, and the
 * drift is worst exactly when the pointer moves fastest.
 */
export function ColumnResizer({ column, label, width, onResize, onReset }: {
  column: string
  /** the human label, for the announcement — "Legal Name", not `legal_name` */
  label: string
  /** the width being dragged from, in px */
  width: number
  onResize: (px: number) => void
  onReset: () => void
}) {
  const origin = useRef<{ x: number; w: number } | null>(null)

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Left button only: a right-click here would otherwise start a drag that no
    // mouseup ever ends.
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()          // never let the grab reach the sort button
    origin.current = { x: e.clientX, w: width }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const o = origin.current
    if (!o) return
    onResize(o.w + (e.clientX - o.x))
  }

  const end = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!origin.current) return
    origin.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 40 : 8
    if (e.key === 'ArrowLeft') { e.preventDefault(); onResize(width - step) }
    else if (e.key === 'ArrowRight') { e.preventDefault(); onResize(width + step) }
    else if (e.key === 'Home') { e.preventDefault(); onReset() }
  }

  return (
    <div
      className={s.grip}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      aria-valuenow={Math.round(width)}
      aria-valuemin={MIN_COL_PX}
      aria-valuemax={MAX_COL_PX}
      tabIndex={0}
      data-testid={`resize-${column}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
      /* Double-click to reset is the convention every spreadsheet has taught,
         and Home does the same for the keyboard. */
      onDoubleClick={(e) => { e.stopPropagation(); onReset() }}
    />
  )
}
